import { useQuery } from '@tanstack/react-query'
import { postNamedQuery } from '../service'
import { useAppContext } from '../redux/slices/appContext'
import { QUERY_KEYS } from './queryKeys'

/*
 * Read-only admin queries against coreData. Both queries are firm-scoped at
 * the BFF + service layer; the UI never passes firmId. Query keys include
 * `userId` so cache entries are isolated when the same browser session
 * switches users.
 */

/**
 * Search registrations with paging, filtering, text search, and sort.
 * Returns the standard server envelope { data, total, hasMore, skip, take }.
 *
 * @param {Object} body - request body for `search-registrations`
 * @param {number} [body.skip=0]
 * @param {number} [body.take=200]
 * @param {{ isActive?: boolean|null }} [body.filters]
 * @param {{ lastName?: any[], longName?: any[], firstName?: any[] }} [body.textSearch]
 * @param {Array<{field: string, dir: 'asc'|'desc'}>} [body.sort]
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true]
 */
export const useSearchRegistrations = (body, options = {}) => {
  const { userId } = useAppContext()

  return useQuery({
    queryKey: [QUERY_KEYS.registration, 'search', userId, body],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'search-registrations', body || {})
      return data
    },
    enabled: options.enabled !== false,
    keepPreviousData: true,
    refetchOnWindowFocus: false
  })
}

/**
 * Fetch a single registration by composite primary key. Disabled until both
 * key parts are present so incomplete URLs don't fire a request.
 *
 * @param {number|string|null|undefined} orionFirmId
 * @param {number|string|null|undefined} orionRegistrationId
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true]
 */
export const useGetRegistration = (orionFirmId, orionRegistrationId, options = {}) => {
  const { userId } = useAppContext()

  const numericFirmId = Number(orionFirmId)
  const numericRegId = Number(orionRegistrationId)
  const hasValidKey =
    Number.isFinite(numericFirmId) && numericFirmId > 0 &&
    Number.isFinite(numericRegId) && numericRegId > 0

  return useQuery({
    queryKey: [QUERY_KEYS.registrationDetail, userId, numericFirmId, numericRegId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-registration', {
        orionFirmId: numericFirmId,
        orionRegistrationId: numericRegId
      })
      return data
    },
    enabled: hasValidKey && (options.enabled !== false),
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Don't retry 404 — the row genuinely doesn't exist or isn't visible.
      if (error?.response?.status === 404) return false
      return failureCount < 2
    }
  })
}

/**
 * List accounts assigned to a single registration. Disabled until both key
 * parts are present so incomplete URLs don't fire a request. The returned
 * data may be incomplete: the `accounts.orionRegistrationId` link column
 * was added recently and may not be fully backfilled — surfaces the
 * "data may be stale" disclaimer in the UI.
 *
 * @param {number|string|null|undefined} orionFirmId
 * @param {number|string|null|undefined} orionRegistrationId
 * @param {Object} [body] - optional pagination / sort
 * @param {number} [body.skip=0]
 * @param {number} [body.take=200]
 * @param {Array<{field: string, dir: 'asc'|'desc'}>} [body.sort]
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true]
 */
export const useSearchAccountsForRegistration = (
  orionFirmId,
  orionRegistrationId,
  body = {},
  options = {}
) => {
  const { userId } = useAppContext()

  const numericFirmId = Number(orionFirmId)
  const numericRegId = Number(orionRegistrationId)
  const hasValidKey =
    Number.isFinite(numericFirmId) && numericFirmId > 0 &&
    Number.isFinite(numericRegId) && numericRegId > 0

  return useQuery({
    queryKey: [QUERY_KEYS.registrationAccounts, userId, numericFirmId, numericRegId, body],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'search-accounts-for-registration', {
        orionFirmId: numericFirmId,
        orionRegistrationId: numericRegId,
        ...body
      })
      return data
    },
    enabled: hasValidKey && (options.enabled !== false),
    keepPreviousData: true,
    refetchOnWindowFocus: false
  })
}
